import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Paper,
  Group,
  Text,
  TextInput,
  Button,
  ActionIcon,
  Modal,
  Stack,
  Select,
  Badge,
  Menu,
  Box,
} from '@mantine/core';
import {
  IconPlus,
  IconSearch,
  IconChevronDown,
  IconLayoutGrid,
  IconList,
  IconColumns,
  IconCalendar,
  IconSettings,
  IconDownload,
  IconX,
} from '@tabler/icons-react';
import { BaseSurface, SurfaceProps } from './SurfaceBase';
import { KanbanView, ListView, GalleryView, CalendarView } from '../views';
import { PropertyManager } from '../properties';
import { PeekContainer } from '../common/PeekContainer';
import { PeekBottomSheet } from '../common/PeekBottomSheet';
import { Chip, ChipRow } from '../common/Chip';
import { PropertyChip, DEFAULT_STATUS_OPTIONS } from '../common/PropertyChip';
import { PageEditor } from '../surfaces/PageEditor';
import { Database, DatabaseItem, createDatabase, createDatabaseItem } from '../../../types/models/Database';
import { loadDatabase, saveDatabase } from '../../../core/workspace/databaseStorage';
import { eventBus } from '../../../core/event-bus';
import { _setPeekState, type PeekChipTabId } from '../common/PeekHooks';

interface DatabaseViewProps extends SurfaceProps {
  database?: Database;
  onDatabaseChange?: (database: Database) => void;
}

/**
 * DatabaseView Component
 * Complete database surface with Gallery, Kanban, List, and Calendar views
 * Uses Core Data Models (Database, DatabaseItem, Property)
 * Includes PeekContainer integration for item preview
 */
export const DatabaseView: React.FC<DatabaseViewProps> = ({
  id = 'database-view',
  phase = 10,
  title = 'Database',
  description = 'Manage your database with multiple views',
  isActive = true,
  isLoading = false,
  error,
  className = '',
  database,
  onDatabaseChange,
}) => {
  // Load database from storage by id (which is the pageId passed in).
  // If the prop is provided, use that. Otherwise load from localStorage.
  const [db, setDb] = useState<Database>(() => {
    if (database) return database;
    const stored = loadDatabase(id);
    return stored || createDatabase(id, title || 'My Database');
  });

  // Peek container state
  const [peekItem, setPeekItem] = useState<DatabaseItem | null>(null);
  const [peekOpen, setPeekOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'content' | 'properties' | 'details'>('content');
  const [peekPulse, setPeekPulse] = useState(false); // triggers pulse animation
  const [showDebug, setShowDebug] = useState(false); // debug JSON collapsed in Details tab
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);

  // Keep isMobile in sync with window resize
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // External Studio integration hooks — listen for fc:peek:* events
  useEffect(() => {
    const handleExternalOpen = ({ cardId }: { cardId: string }) => {
      const item = db.items.find(i => i.id === cardId);
      if (item) {
        setPeekItem(item);
        setPeekOpen(true);
      }
    };
    const handleExternalSelect = ({ cardId }: { cardId: string }) => {
      const item = db.items.find(i => i.id === cardId);
      if (item) {
        setPeekItem(item);
        setPeekOpen(true);
        setActiveTab('content');
        setPeekPulse(true);
        setTimeout(() => setPeekPulse(false), 400);
      }
    };
    const handleExternalClose = () => {
      setPeekOpen(false);
      setPeekItem(null);
    };
    const handleExternalTab = ({ tabId }: { tabId: PeekChipTabId }) => {
      setActiveTab(tabId);
    };
    const handleExternalOpenCS = ({ cardId }: { cardId: string }) => {
      // Open in center stage — could emit navigate event or call workspace method
      setPeekOpen(false);
      setPeekItem(null);
    };

    eventBus.on('fc:peek:open', handleExternalOpen);
    eventBus.on('fc:peek:select', handleExternalSelect);
    eventBus.on('fc:peek:close', handleExternalClose);
    eventBus.on('fc:peek:setTab', handleExternalTab);
    eventBus.on('fc:peek:openCenterStage', handleExternalOpenCS);

    return () => {
      eventBus.off('fc:peek:open', handleExternalOpen);
      eventBus.off('fc:peek:select', handleExternalSelect);
      eventBus.off('fc:peek:close', handleExternalClose);
      eventBus.off('fc:peek:setTab', handleExternalTab);
      eventBus.off('fc:peek:openCenterStage', handleExternalOpenCS);
    };
  }, [db.items]);

  // Emit peek state changes so Studio can sync
  useEffect(() => {
    _setPeekState({ cardId: peekItem?.id ?? null, activeTab, isOpen: peekOpen });
    eventBus.emit('fc:peek:stateChanged', { cardId: peekItem?.id ?? null, activeTab, isOpen: peekOpen });
  }, [peekItem, peekOpen, activeTab]);

  // Persist to storage whenever the database changes
  useEffect(() => {
    if (!database) {
      // Only persist if we're managing state ourselves (i.e. loaded from storage)
      try {
        saveDatabase(db);
      } catch {}
    }
    onDatabaseChange?.(db);
  }, [db, database, onDatabaseChange]);

  // Listen for external 'database:refresh' events (e.g. after a seed populates items
  // in localStorage while this component is already mounted). Without this, the
  // useState lazy initializer would have loaded the empty db at mount time and
  // subsequent saves would overwrite the freshly-seeded items.
  useEffect(() => {
    const handler = (data: any) => {
      if (!data || data.databaseId !== id) return;
      const fresh = loadDatabase(id);
      if (fresh) {
        setDb(fresh);
      }
    };
    eventBus.on('database:refresh', handler);
    return () => eventBus.off('database:refresh', handler);
  }, [id]);

  const [viewMode, setViewMode] = useState<'gallery' | 'kanban' | 'list' | 'calendar'>('kanban');
  const [globalSearch, setGlobalSearch] = useState('');
  const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);
  const [isNewItemOpen, setIsNewItemOpen] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  // Default grouping: 'status' makes the seeded "Quick Notes" demo show
  // Active/Completed columns immediately. Users can change this in the UI;
  // the choice persists via the view-preference localStorage key.
  const [groupByProperty, setGroupByProperty] = useState<string | undefined>('status');

  // Load view preference from localStorage
  useEffect(() => {
    const savedView = localStorage.getItem(`db-view-preference-${db.id}`);
    if (savedView && ['gallery', 'kanban', 'list', 'calendar'].includes(savedView)) {
      setViewMode(savedView as any);
    }
  }, [db.id]);

  // Save view preference to localStorage
  const handleViewModeChange = useCallback((newMode: 'gallery' | 'kanban' | 'list' | 'calendar') => {
    setViewMode(newMode);
    localStorage.setItem(`db-view-preference-${db.id}`, newMode);
  }, [db.id]);

  // Handle database changes
  const handleDatabaseChange = useCallback((updatedDb: Database) => {
    setDb(updatedDb);
    onDatabaseChange?.(updatedDb);
  }, [onDatabaseChange]);

  // When a new card is selected, reset tab to Content and trigger pulse
  useEffect(() => {
    if (peekItem) {
      setActiveTab('content'); // always reset to Content on new card
      setPeekPulse(true);
      const t = setTimeout(() => setPeekPulse(false), 400);
      return () => clearTimeout(t);
    }
  }, [peekItem?.id]);

  const handleItemSelect = useCallback((item: DatabaseItem) => {
    setPeekItem(item);
    setPeekOpen(true);
    eventBus.emit('fc:peek:cardSelected', { cardId: item.id });
  }, []);

  const handlePeekClose = useCallback(() => {
    setPeekOpen(false);
    setPeekItem(null);
    eventBus.emit('fc:peek:panelClosed', {});
  }, []);

  // Filter items
  const filteredItems = useMemo(() => {
    if (!globalSearch) return db.items;

    const searchLower = globalSearch.toLowerCase();
    return db.items.filter(item =>
      item.title.toLowerCase().includes(searchLower) ||
      item.properties.some(prop =>
        String(prop.value).toLowerCase().includes(searchLower)
      )
    );
  }, [db.items, globalSearch]);

  // Handle add item
  const handleAddItem = () => {
    if (!newItemTitle.trim()) return;

    const newItem = createDatabaseItem(
      `item_${Date.now()}`,
      db.id,
      newItemTitle
    );

    if (groupByProperty) {
      newItem.properties.push({
        propertyId: groupByProperty,
        value: undefined,
      });
    }

    const updatedDb = {
      ...db,
      items: [...db.items, newItem],
      updatedAt: new Date(),
    };

    handleDatabaseChange(updatedDb);
    setIsNewItemOpen(false);
    setNewItemTitle('');
  };

  // Handle update item
  const handleUpdateItem = (item: DatabaseItem) => {
    const updatedDb = {
      ...db,
      items: db.items.map(i => (i.id === item.id ? item : i)),
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
    // Update peek item if it's the same item
    if (peekItem?.id === item.id) {
      setPeekItem(item);
    }
  };

  // Handle delete item
  const handleDeleteItem = (itemId: string) => {
    const updatedDb = {
      ...db,
      items: db.items.filter(i => i.id !== itemId),
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
    // Clear peek if it's the deleted item
    if (peekItem?.id === itemId) {
      setPeekItem(null);
      setPeekOpen(false);
    }
  };

  // Handle duplicate item
  const handleDuplicateItem = (item: DatabaseItem) => {
    const newItem: DatabaseItem = {
      ...item,
      id: `item_${Date.now()}`,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const updatedDb = {
      ...db,
      items: [...db.items, newItem],
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
  };

  // Handle property change
  const handlePropertyChange = (itemId: string, propertyId: string, value: any) => {
    const updatedDb = {
      ...db,
      items: db.items.map(i => {
        if (i.id !== itemId) return i;
        return {
          ...i,
          properties: i.properties.map(p =>
            p.propertyId === propertyId ? { ...p, value } : p
          ),
          updatedAt: new Date(),
        };
      }),
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
    // Update peek item if it's the same item
    if (peekItem?.id === itemId) {
      const updatedItem = updatedDb.items.find(i => i.id === itemId);
      if (updatedItem) {
        setPeekItem(updatedItem);
      }
    }
  };

  // Handle properties change
  const handlePropertiesChange = (properties: any[]) => {
    const updatedDb = {
      ...db,
      schema: {
        ...db.schema,
        properties,
      },
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
  };

  // Handle export
  const handleExport = () => {
    const dataStr = JSON.stringify(db, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `database_${db.id}_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Peek Chip (Back of Card) — 3-tab chip navigation
  const peekChipTabBar = (
    <Box style={{ padding: '12px 16px 0', borderBottom: '1px solid var(--fc-border-secondary)' }}>
      <ChipRow>
        <Chip
          label="Content"
          state={activeTab === 'content' ? 'active' : 'inactive'}
          onClick={() => setActiveTab('content')}
        />
        <Chip
          label="Properties"
          state={activeTab === 'properties' ? 'active' : 'inactive'}
          onClick={() => setActiveTab('properties')}
        />
        <Chip
          label="Details"
          state={activeTab === 'details' ? 'active' : 'inactive'}
          onClick={() => setActiveTab('details')}
        />
      </ChipRow>
    </Box>
  );

  // Tab 1: Content — PageEditor (Card=Page model: DatabaseItem.id = Page.id)
  const contentTab = (
    <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {peekItem ? (
        <PageEditor
          pageId={peekItem.id}
          peekMode={true}
          className="peek-content-editor"
        />
      ) : (
        <Box style={{ padding: 16, textAlign: 'center', color: 'var(--fc-text-muted)' }}>
          <Text size="sm" c="dimmed">No card selected</Text>
        </Box>
      )}
      {/* Support-act chips */}
      <Box style={{ padding: '8px 16px', borderTop: '1px solid var(--fc-border-secondary)' }}>
        <ChipRow>
          <Chip label="Copy link" variant="tag" onClick={() => { navigator.clipboard.writeText(`freshcards://card/${peekItem?.id}`); }} />
        </ChipRow>
      </Box>
    </Box>
  );

  // Tab 2: Properties — PropertyChip row
  const propertiesTab = (
    <Box style={{ padding: '16px' }}>
      {peekItem && db.schema.properties.length === 0 && (
        <Text size="sm" c="dimmed" mb="md">No properties yet. Add them in the database schema.</Text>
      )}
      {peekItem && db.schema.properties.length > 0 && (
        <>
          <Stack gap="xs">
            {db.schema.properties.map(propDef => {
              const propVal = peekItem.properties.find(p => p.propertyId === propDef.id);
              return (
                <Box key={propDef.id}>
                  <Text size="xs" c="dimmed" mb={4}>{propDef.name}</Text>
                  <PropertyChip
                    property={propDef}
                    value={propVal?.value}
                    onChange={(newVal) => handlePropertyChange(peekItem.id, propDef.id, newVal)}
                  />
                </Box>
              );
            })}
          </Stack>
          {/* Support-act chips */}
          <ChipRow style={{ marginTop: 12 }}>
            <Chip label="+ Add property" variant="add" disabled />
            <Chip label="Manage schema" variant="tag" disabled />
          </ChipRow>
        </>
      )}
    </Box>
  );

  // Tab 3: Details — metadata display
  const detailsTab = (
    <Box style={{ padding: '16px' }}>
      <Stack gap="xs">
        <Group justify="space-between">
          <Text size="xs" c="dimmed">Created</Text>
          <Text size="sm">{peekItem?.createdAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</Text>
        </Group>
        <Group justify="space-between">
          <Text size="xs" c="dimmed">Updated</Text>
          <Text size="sm">{peekItem?.updatedAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</Text>
        </Group>
        <Group justify="space-between">
          <Text size="xs" c="dimmed">Database</Text>
          <Text size="sm">{db.name}</Text>
        </Group>
        <Group justify="space-between">
          <Text size="xs" c="dimmed">Card ID</Text>
          <Text size="xs" style={{ fontFamily: 'var(--fc-font-mono)' }}>{peekItem?.id}</Text>
        </Group>
      </Stack>
      {/* Tags section */}
      <Box mt="md">
        <Text size="xs" c="dimmed" mb="xs" tt="uppercase" fw={500}>Tags</Text>
        <ChipRow>
          {(peekItem?.tags || []).length === 0 && (
            <Text size="xs" c="dimmed">No tags</Text>
          )}
          {(peekItem?.tags || []).map(tag => (
            <Chip key={tag} label={tag} variant="tag" />
          ))}
        </ChipRow>
      </Box>
      {/* Debug JSON */}
      <Box mt="md">
        <Text
          size="xs"
          c="dimmed"
          tt="uppercase"
          fw={500}
          mb="xs"
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setShowDebug(o => !o)}
        >
          Debug {showDebug ? '▲' : '▼'}
        </Text>
        {showDebug && (
          <Paper p="sm" radius="md" withBorder style={{ background: 'var(--fc-bg-secondary)', maxHeight: 200, overflow: 'auto' }}>
            <Text size="xs" style={{ fontFamily: 'var(--fc-font-mono)', whiteSpace: 'pre' }}>
              {JSON.stringify(peekItem, null, 2)}
            </Text>
          </Paper>
        )}
      </Box>
      {/* Support-act chips */}
      <ChipRow style={{ marginTop: 12 }}>
        <Chip label="Copy Card ID" variant="tag" onClick={() => navigator.clipboard.writeText(peekItem?.id || '')} />
        <Chip label="Copy link" variant="tag" onClick={() => navigator.clipboard.writeText(`freshcards://card/${peekItem?.id}`)} />
      </ChipRow>
    </Box>
  );

  // New peek content with 3-tab structure + pulse animation
  const peekContent = useMemo(() => {
    if (!peekItem) return null;
    return (
      <Box
        className={`peek-chip-content ${peekPulse ? 'pulse' : ''}`}
        style={{
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          animation: peekPulse ? 'fc-peek-pulse 0.4s ease-out' : 'none',
        }}
      >
        {/* ChipNav tab bar */}
        {peekChipTabBar}
        {/* Tab content */}
        <Box style={{ flex: 1, overflowY: 'auto' }}>
          {activeTab === 'content' && contentTab}
          {activeTab === 'properties' && propertiesTab}
          {activeTab === 'details' && detailsTab}
        </Box>
      </Box>
    );
  }, [peekItem, activeTab, peekPulse, showDebug, db]);

  return (
    <BaseSurface
      id={id}
      phase={phase}
      title={title}
      description={description}
      isActive={isActive}
      isLoading={isLoading}
      error={error || ''}
      className={`database-view-surface-wrapper ${className}`}
    >
      <div className="database-view-content" style={{ height: '100%', position: 'relative' }}>
        {/* Peek panel — bottom sheet on mobile, side panel on desktop */}
        {isMobile ? (
          <PeekBottomSheet
            id="db-item-preview"
            title={peekItem?.title || 'Item Preview'}
            isOpen={peekOpen}
            onClose={handlePeekClose}
          >
            {peekContent}
          </PeekBottomSheet>
        ) : (
          <PeekContainer
            id="db-item-preview"
            title={peekItem?.title || 'Item Preview'}
            isOpen={peekOpen}
            onClose={handlePeekClose}
            width={320}
          >
            {peekContent}
          </PeekContainer>
        )}

        {/* Toolbar */}
        <Paper p="md" mb="md" radius="md" withBorder>
          <Group justify="space-between" align="center" wrap="wrap">
            <Group gap="sm">
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => setIsNewItemOpen(true)}
              >
                New Item
              </Button>
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <Button
                    variant="subtle"
                    rightSection={<IconChevronDown size={14} />}
                  >
                    View
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconLayoutGrid size={16} />}
                    onClick={() => handleViewModeChange('gallery')}
                  >
                    Gallery
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconColumns size={16} />}
                    onClick={() => handleViewModeChange('kanban')}
                  >
                    Kanban
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconList size={16} />}
                    onClick={() => handleViewModeChange('list')}
                  >
                    List
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconCalendar size={16} />}
                    onClick={() => handleViewModeChange('calendar')}
                  >
                    Calendar
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>

            <TextInput
              placeholder="Search items..."
              leftSection={<IconSearch size={16} />}
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.currentTarget.value)}
              style={{ flex: 1, maxWidth: '300px' }}
            />

            <Group gap="sm">
              <Badge variant="light">
                {filteredItems.length} items
              </Badge>
              <Button
                leftSection={<IconDownload size={16} />}
                variant="light"
                size="sm"
                onClick={handleExport}
              >
                Export
              </Button>
              <Button
                leftSection={<IconSettings size={16} />}
                variant="light"
                size="sm"
                onClick={() => setIsPropertiesOpen(true)}
              >
                Properties
              </Button>
            </Group>
          </Group>
        </Paper>

        {/* Active View Indicator */}
        <Group justify="flex-start" mb="sm" gap="xs">
          {viewMode === 'gallery' && <Badge>Gallery View</Badge>}
          {viewMode === 'kanban' && <Badge>Kanban View</Badge>}
          {viewMode === 'list' && <Badge>List View</Badge>}
          {viewMode === 'calendar' && <Badge>Calendar View</Badge>}
        </Group>

        {/* Views */}
        <div style={{ width: '100%' }}>
          {viewMode === 'gallery' && (
            <GalleryView
              database={db}
              items={filteredItems}
              onItemSelect={handleItemSelect}
              onItemCreate={handleAddItem}
              onItemUpdate={handleUpdateItem}
              onItemDelete={handleDeleteItem}
              onItemDuplicate={handleDuplicateItem}
              onPropertyChange={handlePropertyChange}
            />
          )}

          {viewMode === 'kanban' && (
            <KanbanView
              database={db}
              items={filteredItems}
              groupByPropertyId={groupByProperty}
              onItemSelect={handleItemSelect}
              onItemCreate={handleAddItem}
              onItemUpdate={handleUpdateItem}
              onItemDelete={handleDeleteItem}
              onItemDuplicate={handleDuplicateItem}
              onPropertyChange={handlePropertyChange}
            />
          )}

          {viewMode === 'list' && (
            <ListView
              database={db}
              items={filteredItems}
              onItemSelect={handleItemSelect}
              onItemUpdate={handleUpdateItem}
              onItemDelete={handleDeleteItem}
              onItemDuplicate={handleDuplicateItem}
            />
          )}

          {viewMode === 'calendar' && (
            <CalendarView
              database={db}
              items={filteredItems}
              datePropertyId={
                db.schema.properties.find(p => p.type === 'date')?.id
              }
              onItemSelect={handleItemSelect}
            />
          )}
        </div>

        {/* New Item Modal */}
        <Modal
          opened={isNewItemOpen}
          onClose={() => {
            setIsNewItemOpen(false);
            setNewItemTitle('');
          }}
          title="Create New Item"
          size="sm"
        >
          <Stack>
            <TextInput
              label="Item Title"
              placeholder="Enter item title"
              value={newItemTitle}
              onChange={(e) => setNewItemTitle(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddItem();
              }}
              autoFocus
            />
            <Group justify="flex-end" gap="sm">
              <Button
                variant="subtle"
                onClick={() => {
                  setIsNewItemOpen(false);
                  setNewItemTitle('');
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleAddItem} disabled={!newItemTitle.trim()}>
                Create
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Properties Modal */}
        <Modal
          opened={isPropertiesOpen}
          onClose={() => setIsPropertiesOpen(false)}
          title="Database Properties"
          size="md"
          scrollAreaComponent={Paper}
        >
          <PropertyManager
            properties={db.schema.properties}
            onPropertiesChange={handlePropertiesChange}
          />

          {/* Kanban grouping option */}
          <Paper p="md" mt="lg" radius="md" withBorder>
            <Text fw={500} mb="xs">
              Kanban Settings
            </Text>
            <Select
              label="Group by property"
              placeholder="Select a property"
              data={[
                { value: '', label: 'None' },
                ...db.schema.properties
                  .filter(p => p.type === 'select' || p.type === 'status')
                  .map(p => ({ value: p.id, label: p.name })),
              ]}
              value={groupByProperty || ''}
              onChange={(val) => setGroupByProperty(val || undefined)}
            />
          </Paper>
        </Modal>
      </div>
    </BaseSurface>
  );
};

export default DatabaseView;